//
// A part of a text: plain, or a web address which is shown as a link.
//
export interface TextSegment
{
  text: string;
  url: string | null;
}

//
// Only http and https addresses are linked, and the text of the link is the address itself. The text is
// rendered by interpolation, so nothing in a message can become markup.
//
const WEB_ADDRESS = /https?:\/\/[^\s<>"']*[^\s<>"'.,;:!?)\]]/gi;

export function splitWebAddresses(text: string | null | undefined): TextSegment[]
{
  const value = text ?? '';
  const segments: TextSegment[] = [];
  let last = 0;

  for (const match of value.matchAll(WEB_ADDRESS))
  {
    if (match.index > last)
    {
      segments.push({ text: value.substring(last, match.index), url: null });
    }

    segments.push({ text: match[0], url: match[0] });
    last = match.index + match[0].length;
  }

  if (last < value.length)
  {
    segments.push({ text: value.substring(last), url: null });
  }

  return segments;
}

//
// The invitation to the forums is one translated sentence, so the word order is the language's own; the
// address of the forums is found in it afterwards and becomes the link.
//
const FORUMS_HOST = /forums\.invantive\.(?:com|eu)/;

export function splitForumsHost(text: string | null | undefined): TextSegment[]
{
  const value = text ?? '';
  const match = FORUMS_HOST.exec(value);

  if (!match)
  {
    return value ? [{ text: value, url: null }] : [];
  }

  return [
    { text: value.substring(0, match.index), url: null }
  , { text: match[0], url: 'https://' + match[0] }
  , { text: value.substring(match.index + match[0].length), url: null }
  ].filter(segment => segment.text !== '');
}

//
// A time as the service writes it, shown in UTC and named so, as the error window of the other products does.
//
export function formatUtcTime(value: string | null | undefined): string
{
  if (!value)
  {
    return '';
  }

  const date = new Date(/(Z|[+-][0-9]{2}:?[0-9]{2})$/i.test(value) ? value : value + 'Z');

  return Number.isNaN(date.getTime()) ? '' : date.toISOString().substring(0, 19).replace('T', ' ') + ' UTC';
}

//
// The copy command is asked first, because it only works on the click which asked for it and a browser can
// refuse the clipboard interface even there, such as a frame without the permission. The interface follows
// for a browser without the command.
//
export function copyTextToClipboard(text: string): Promise<void>
{
  return copyTextWithCommand(text).catch(() => navigator.clipboard && window.isSecureContext ? navigator.clipboard.writeText(text) : Promise.reject(new Error('copy')));
}

//
// A hidden text area and the copy command; focus returns to the control it was on, so a keyboard user keeps
// their place in the message.
//
function copyTextWithCommand(text: string): Promise<void>
{
  return new Promise((resolve, reject) =>
  {
    const previous = document.activeElement;
    const area = document.createElement('textarea');

    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();

    try
    {
      if (document.execCommand('copy'))
      {
        resolve();
      }
      else
      {
        reject(new Error('copy'));
      }
    }
    catch (error)
    {
      reject(error);
    }
    finally
    {
      document.body.removeChild(area);

      if (previous instanceof HTMLElement)
      {
        previous.focus();
      }
    }
  });
}
